import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useCanonicalUrl } from '../components/seo/SchemaMarkup';

const SECTIONS = [
  {
    title: '1. Information We Collect',
    body: 'We collect information you provide directly: account details (name, email), order and shipping information, community posts, and messages you send us. We automatically collect standard usage data — pages visited, device type, and approximate location — to improve the site.',
  },
  {
    title: '2. Payment Information',
    body: 'We never store full card numbers. All payments are processed by Stripe, a PCI-DSS Level 1 certified payment provider. We retain only order metadata (amount, items, last 4 digits where provided by Stripe) needed for fulfillment and support.',
  },
  {
    title: '3. How We Use Your Information',
    body: 'We use your data to fulfill orders, operate your account and rewards balance, send transactional emails (order confirmations, shipping updates), personalize content, and — only with your consent — send marketing emails. You can unsubscribe at any time.',
  },
  {
    title: '4. Cookies & Analytics',
    body: 'We use cookies for cart persistence, login sessions, and anonymized analytics. You can disable cookies in your browser; core shopping features may be affected.',
  },
  {
    title: '5. AI Shopping Agents',
    body: 'When a purchase is initiated through an AI shopping agent (such as ChatGPT Instant Checkout), we receive only the data needed to fulfill the order: items, shipping address, buyer name/email, and a tokenized payment credential. We treat this data identically to direct orders.',
  },
  {
    title: '6. Sharing',
    body: 'We do not sell your personal data. We share it only with service providers required to operate the business: Stripe (payments), shipping carriers (delivery), Resend (transactional email), and Redo (returns). Each is bound to use your data solely to provide their service.',
  },
  {
    title: '7. Data Retention & Your Rights',
    body: 'Order records are retained for tax and warranty purposes. You may request access to, correction of, or deletion of your personal data at any time by emailing info@ninjathlete.com. California and EU residents have additional rights under CCPA/GDPR, which we honor.',
  },
  {
    title: '8. Security',
    body: 'All traffic is encrypted via TLS. Access to customer data is restricted to authorized personnel. No system is 100% secure — report suspected vulnerabilities to info@ninjathlete.com.',
  },
  {
    title: '9. Changes & Contact',
    body: 'We may update this policy as the site evolves; material changes will be noted on this page. Questions? Email info@ninjathlete.com.',
  },
];

export default function PrivacyPage() {
  useCanonicalUrl();
  return (
    <main className="pt-20 min-h-screen bg-black text-white" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-8" data-testid="privacy-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <h1 className="text-4xl sm:text-5xl font-bold uppercase tracking-tight mb-3">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-12">Last updated: June 12, 2026 · NinjAthlete, Dallas, TX</p>
        <div className="space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="text-lg font-bold mb-2 text-red-500">{s.title}</h2>
              <p className="text-gray-300 leading-relaxed text-sm">{s.body}</p>
            </section>
          ))}
        </div>
        <div className="mt-14 pt-8 border-t border-gray-800 text-sm text-gray-400">
          Related: <Link to="/terms" className="text-red-400 hover:text-red-300 underline">Terms of Service</Link>
          {' · '}<Link to="/returns" className="text-red-400 hover:text-red-300 underline">Returns</Link>
          {' · '}<Link to="/editorial-policy" className="text-red-400 hover:text-red-300 underline">Editorial Policy</Link>
        </div>
      </div>
    </main>
  );
}
